import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  CreditCard, 
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  TrendingUp
} from 'lucide-react';
import { accountsPayableService } from '@/services/accountsPayableService';
import type { AccountPayableStats } from '@/types/accounts-payable.types';

interface AccountsPayableStatsProps {
  stats: AccountPayableStats | null;
  loading: boolean;
}

export const AccountsPayableStats: React.FC<AccountsPayableStatsProps> = ({ 
  stats, 
  loading 
}) => {
  if (loading || !stats) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </CardTitle>
              <div className="h-4 w-4 bg-gray-200 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-200 rounded w-20 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-32"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const statsCards = [
    {
      title: 'Total de Cuentas',
      value: stats.total.toString(),
      description: 'Cuentas por pagar registradas',
      icon: CreditCard,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Monto Total',
      value: accountsPayableService.formatCurrency(parseFloat(stats.totalAmount)),
      description: `${stats.paid} pagadas, ${stats.pending} pendientes`,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Pendiente de Pago',
      value: accountsPayableService.formatCurrency(parseFloat(stats.pendingAmount)),
      description: `${stats.pending + stats.partiallyPaid} cuentas por pagar`,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      title: 'Vencidas',
      value: accountsPayableService.formatCurrency(parseFloat(stats.overdueAmount)),
      description: `${stats.overdueCount} cuentas vencidas`,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {statsCards.map((stat, index) => (
        <Card key={index}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              {stat.title}
            </CardTitle>
            <div className={`${stat.bgColor} ${stat.color} p-2 rounded-lg`}>
              <stat.icon className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stat.color} mb-1`}>
              {stat.value}
            </div>
            <p className="text-xs text-gray-600">
              {stat.description}
            </p>
            
            {/* Indicador de estado especial para cuentas vencidas */}
            {index === 3 && stats.overdueCount > 0 && (
              <Badge variant="destructive" className="mt-2 text-xs">
                Requiere atención
              </Badge>
            )}
          </CardContent>
        </Card>
      ))}

    </div>
  );
};